// components/advertiser-wallet/SpendTrendChart.tsx
import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";

interface SpendTrendChartProps {
  data: { day: string; value: number }[];
}

const SpendTrendChart: React.FC<SpendTrendChartProps> = ({ data }) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return;

    const chart = echarts.init(chartRef.current);

    const days = data.map(item => item.day);
    const values = data.map(item => item.value);

    const option = {
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#ffffff',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        textStyle: {
          color: '#121212',
          fontSize: 12
        },
        formatter: (params: any) => {
          const param = params[0];
          const value = param.value >= 1000 ? `₦${(param.value / 1000).toFixed(1)}K` : `₦${param.value}`;
          return `<div style="font-weight: bold; margin-bottom: 5px;">${param.axisValue}</div>
                  <div>${value}</div>`;
        }
      },
      grid: {
        left: '5%',
        right: '5%',
        top: '5%',
        bottom: '5%',
        containLabel: true
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: days,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          fontSize: 12,
          color: '#9CA3AF',
        },
      },
      yAxis: {
        type: "value",
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: {
          show: true,
          lineStyle: { color: "#f0f0f0", type: 'dashed' },
        },
        axisLabel: {
          fontSize: 12,
          color: '#9CA3AF',
          formatter: (value: number) => value === 0 ? '0' : `₦${value / 1000}K`
        }
      },
      series: [
        {
          name: "Spend",
          data: values,
          type: "line",
          smooth: true,
          symbol: "circle",
          symbolSize: 6,
          lineStyle: {
            color: "#10b981",
            width: 3,
          },
          itemStyle: {
            color: "#10b981",
            borderColor: "#ffffff",
            borderWidth: 2,
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: "rgba(16, 185, 129, 0.35)" },
              { offset: 1, color: "rgba(16, 185, 129, 0)" },
            ]),
          },
        }
      ],
    };

    chart.setOption(option);

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
    };
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-gray-400 text-sm">
        No data available
      </div>
    );
  }

  return <div ref={chartRef} style={{ height: "100%", width: "100%" }} />;
};

export default SpendTrendChart;